import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  X, Check, Loader, AlertCircle, Eye, EyeOff,
  SkipForward, Plus, RefreshCw, Download
} from 'lucide-react';
import jobPhotoService from '../../services/jobPhotoService';

const MAX_EDGE = 3000;
const JPEG_QUALITY = 0.9;
const STARTER_LABELS = ['Overview', 'Front', 'Back', 'Left side', 'Right side'];
const LABELS_KEY = 'sf_photo_labels_v1';
const SAVE_COPY_KEY = 'sf_photo_save_copy_v1';
const GHOST_LEVELS = [0.4, 0.7, 0];

const readJson = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable - nothing breaks without it */
  }
};

// "No after needed" list, stored on this device per job
export const getSkippedBeforeIds = (jobId) => {
  const ids = readJson(`sf_skip_after_${jobId}`, []);
  return Array.isArray(ids) ? ids : [];
};

export const saveSkippedBeforeIds = (jobId, ids) => writeJson(`sf_skip_after_${jobId}`, ids);

const loadRecentLabels = () => {
  const list = readJson(LABELS_KEY, []);
  return Array.isArray(list) ? list.filter(l => l && l.label) : [];
};

const rememberLabel = (label) => {
  const list = loadRecentLabels();
  const found = list.find(l => l.label.toLowerCase() === label.toLowerCase());
  if (found) found.count += 1;
  else list.push({ label, count: 1 });
  list.sort((a, b) => b.count - a.count);
  writeJson(LABELS_KEY, list.slice(0, 30));
};

/**
 * PhotoCapture - full-screen in-app camera for job photos.
 *  mode "Before": shoot repeatedly, tag each shot with an area label.
 *  mode "After":  pick a Before, line the live camera up with its ghost image, shoot.
 *                 The After is paired automatically and the next unmatched Before is selected.
 * Shots are downscaled, queued, and uploaded one at a time with retry.
 */
const PhotoCapture = ({
  jobId,
  mode,
  photos,
  startBeforeId = null,
  skippedIds = [],
  onSkippedChange,
  onPhotoAdded,
  onClose,
}) => {
  const isAfter = mode === 'After';

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const coordsRef = useRef({ latitude: null, longitude: null });
  const queueRef = useRef([]);
  const processingRef = useRef(false);
  const noticeTimer = useRef(null);
  const onPhotoAddedRef = useRef(onPhotoAdded);
  onPhotoAddedRef.current = onPhotoAdded;

  const [ready, setReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(false);
  const [notice, setNotice] = useState('');
  const [shotCount, setShotCount] = useState(0);
  const [pending, setPending] = useState(0);
  const [failed, setFailed] = useState([]);
  const [showCloseGuard, setShowCloseGuard] = useState(false);
  const [saveCopy, setSaveCopy] = useState(() => readJson(SAVE_COPY_KEY, true) !== false);
  const [label, setLabel] = useState('');
  const [showLabelInput, setShowLabelInput] = useState(false);
  const [labelDraft, setLabelDraft] = useState('');
  const [selectedBeforeId, setSelectedBeforeId] = useState(startBeforeId);
  const [shotIds, setShotIds] = useState(() => new Set());
  const [ghostLevel, setGhostLevel] = useState(0);

  // ---------- derived data ----------
  const skipped = useMemo(() => new Set(skippedIds), [skippedIds]);

  const pairedIds = useMemo(
    () => new Set(photos.filter(p => p.type === 'After' && p.pairedWithPhotoId).map(p => p.pairedWithPhotoId)),
    [photos]
  );

  // Ids grow in upload order, so this is the order the befores were shot
  const beforeList = useMemo(
    () => photos.filter(p => p.type === 'Before').sort((a, b) => a.id - b.id),
    [photos]
  );

  const needsAfter = useMemo(
    () => beforeList.filter(b => !pairedIds.has(b.id) && !shotIds.has(b.id) && !skipped.has(b.id)),
    [beforeList, pairedIds, shotIds, skipped]
  );

  const stripList = useMemo(() => beforeList.filter(b => !skipped.has(b.id)), [beforeList, skipped]);
  const skippedHere = beforeList.length - stripList.length;
  const selectedBefore = beforeList.find(b => b.id === selectedBeforeId) || null;

  // Label chips: used on this job, then your recent labels, then a neutral starter set
  const labelOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    const add = (text) => {
      const t = (text || '').trim();
      if (!t) return;
      const key = t.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      out.push(t);
    };
    [...photos].reverse().forEach(p => add(p.caption));
    loadRecentLabels().forEach(l => add(l.label));
    STARTER_LABELS.forEach(add);
    const limited = out.slice(0, 16);
    if (label && !limited.some(l => l.toLowerCase() === label.toLowerCase())) limited.unshift(label);
    return limited;
  }, [photos, label]);

  // ---------- camera lifecycle ----------
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => {
    let cancelled = false;

    const start = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setCameraError('This browser cannot open the camera here. In-app capture needs a secure (https) connection.');
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 4096 },
            height: { ideal: 2160 },
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          await video.play().catch(() => {});
        }
        setReady(true);
      } catch (err) {
        setCameraError(
          err?.name === 'NotAllowedError'
            ? 'Camera permission is blocked. Allow camera access for this site in your browser settings, then reopen.'
            : 'Could not start the camera.'
        );
      }
    };

    start();
    return () => {
      cancelled = true;
      stopCamera();
    };
  }, []);

  // One location fix when the camera opens, reused for every shot
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        coordsRef.current = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
      },
      () => {},
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 300000 }
    );
  }, []);

  // Keep the screen awake while shooting (ignored where unsupported)
  useEffect(() => {
    let lock = null;
    const acquire = async () => {
      try {
        lock = (await navigator.wakeLock?.request('screen')) || null;
      } catch {
        /* not supported */
      }
    };
    acquire();
    return () => {
      lock?.release?.()?.catch?.(() => {});
    };
  }, []);

  // Lock page scroll while open
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  useEffect(() => () => clearTimeout(noticeTimer.current), []);

  // After mode: always have a valid Before selected
  useEffect(() => {
    if (!isAfter) return;
    if (!beforeList.some(b => b.id === selectedBeforeId)) {
      setSelectedBeforeId(needsAfter.length > 0 ? needsAfter[0].id : null);
    }
  }, [isAfter, beforeList, needsAfter, selectedBeforeId]);

  // Keep the selected thumbnail visible in the strip
  useEffect(() => {
    if (selectedBeforeId == null) return;
    document.getElementById(`sf-before-${selectedBeforeId}`)
      ?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  }, [selectedBeforeId]);

  // ---------- upload queue ----------
  const uploadItem = async (item) => {
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const res = await jobPhotoService.uploadPhoto(
          jobId, item.type, item.file, item.caption,
          item.latitude, item.longitude, item.pairedWithPhotoId, item.takenAt
        );
        return { ok: true, data: res.data };
      } catch (err) {
        const status = err.response?.status;
        // The server said no (limit reached, bad pairing, etc.) - retrying will not change that
        if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
          return { ok: false, message: err.response?.data?.message || 'The server rejected this photo.' };
        }
        if (attempt === 3) {
          return { ok: false, message: 'Could not reach the server. Retry when you have signal.' };
        }
        await new Promise(resolve => setTimeout(resolve, 1500 * attempt));
      }
    }
    return { ok: false, message: 'Upload failed.' };
  };

  const processQueue = async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    while (queueRef.current.length > 0) {
      const item = queueRef.current[0];
      const result = await uploadItem(item);
      queueRef.current.shift();
      setPending(queueRef.current.length);
      if (result.ok) {
        onPhotoAddedRef.current?.(result.data);
      } else {
        setFailed(prev => [...prev, { ...item, message: result.message }]);
        if (item.type === 'After' && item.pairedWithPhotoId) {
          setShotIds(prev => {
            const next = new Set(prev);
            next.delete(item.pairedWithPhotoId);
            return next;
          });
        }
      }
    }
    processingRef.current = false;
  };

  const enqueue = (item) => {
    queueRef.current.push(item);
    setPending(queueRef.current.length);
    processQueue();
  };

  const retryFailed = () => {
    if (failed.length === 0) return;
    const items = failed;
    setFailed([]);
    items.forEach(({ message, ...item }) => queueRef.current.push(item));
    setPending(queueRef.current.length);
    processQueue();
  };

  // ---------- capture ----------
  const showNotice = (text) => {
    setNotice(text);
    clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(''), 2500);
  };

  const captureFrame = () => new Promise((resolve, reject) => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) {
      reject(new Error('Camera not ready'));
      return;
    }
    const scale = Math.min(1, MAX_EDGE / Math.max(video.videoWidth, video.videoHeight));
    const width = Math.round(video.videoWidth * scale);
    const height = Math.round(video.videoHeight * scale);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d').drawImage(video, 0, 0, width, height);
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Capture failed'))),
      'image/jpeg',
      JPEG_QUALITY
    );
  });

  const downloadCopy = (file) => {
    try {
      const url = URL.createObjectURL(file);
      const a = document.createElement('a');
      a.href = url;
      a.download = `SuddyFlow-job${jobId}-${file.name}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      /* the device copy is a bonus - never block the shot */
    }
  };

  const nextAfterId = (doneId) => {
    const remaining = needsAfter.filter(b => b.id !== doneId);
    const next = remaining.find(b => b.id > doneId) || remaining[0] || null;
    return next ? next.id : null;
  };

  const handleShutter = async () => {
    if (!ready || busy) return;
    if (isAfter && !selectedBefore) return;
    setBusy(true);
    try {
      const blob = await captureFrame();
      const takenAt = new Date().toISOString();
      const stamp = takenAt.replace(/\D/g, '').slice(0, 14);
      const type = isAfter ? 'After' : 'Before';
      const file = new File([blob], `${type.toLowerCase()}-${stamp}.jpg`, { type: 'image/jpeg' });
      const caption = isAfter ? (selectedBefore.caption || null) : (label || null);

      setFlash(true);
      setTimeout(() => setFlash(false), 120);

      if (!isAfter && label) rememberLabel(label);
      if (saveCopy) downloadCopy(file);

      enqueue({
        file,
        type,
        caption,
        takenAt,
        latitude: coordsRef.current.latitude,
        longitude: coordsRef.current.longitude,
        pairedWithPhotoId: isAfter ? selectedBefore.id : null,
      });
      setShotCount(c => c + 1);

      if (isAfter) {
        const doneId = selectedBefore.id;
        setShotIds(prev => new Set(prev).add(doneId));
        setSelectedBeforeId(nextAfterId(doneId));
      }
    } catch {
      showNotice('That shot did not capture. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleSkipSelected = () => {
    if (!selectedBefore) return;
    const id = selectedBefore.id;
    onSkippedChange?.([...new Set([...skippedIds, id])]);
    setSelectedBeforeId(nextAfterId(id));
  };

  const toggleSaveCopy = () => {
    const next = !saveCopy;
    setSaveCopy(next);
    writeJson(SAVE_COPY_KEY, next);
    showNotice(next ? 'Saving a copy of each shot to this device' : 'Not saving copies to this device');
  };

  // ---------- closing ----------
  const requestClose = () => {
    if (pending === 0 && failed.length === 0) {
      stopCamera();
      onClose();
    } else {
      setShowCloseGuard(true);
    }
  };

  useEffect(() => {
    if (showCloseGuard && pending === 0 && failed.length === 0) {
      stopCamera();
      onClose();
    }
  }, [showCloseGuard, pending, failed.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') requestClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  // ---------- render ----------
  const ghostOpacity = GHOST_LEVELS[ghostLevel];
  const allMatched = isAfter && !selectedBefore && !cameraError;

  const content = (
    <div
      className="fixed inset-0 z-[100] bg-black flex flex-col"
      style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 text-white">
        <button
          onClick={requestClose}
          className="p-2 rounded-full bg-white/10 hover:bg-white/20"
          aria-label="Close camera"
        >
          <X size={22} />
        </button>

        <div className="flex-1 text-center min-w-0">
          <p className="text-sm font-semibold truncate">
            {isAfter
              ? (needsAfter.length > 0 ? `After photos · ${needsAfter.length} left` : 'After photos')
              : 'Before photos'}
          </p>
          <p className="text-xs text-white/60">{shotCount} shot this session</p>
        </div>

        <button
          onClick={toggleSaveCopy}
          className={`p-2 rounded-full ${saveCopy ? 'bg-green-600 text-white' : 'bg-white/10 text-white/70'}`}
          aria-label="Save a copy of each shot to this device"
          title="Save a copy of each shot to this device"
        >
          <Download size={22} />
        </button>
      </div>

      {/* Status row */}
      {(pending > 0 || failed.length > 0 || notice) && (
        <div className="px-3 pb-2 flex flex-wrap items-center justify-center gap-2 text-xs">
          {pending > 0 && (
            <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-white/15 text-white">
              <Loader size={12} className="animate-spin" /> Uploading {pending}…
            </span>
          )}
          {failed.length > 0 && (
            <button
              onClick={retryFailed}
              className="flex items-center gap-1 px-2 py-1 rounded-full bg-red-600 text-white"
            >
              <RefreshCw size={12} /> {failed.length} failed · Retry
            </button>
          )}
          {notice && <span className="px-2 py-1 rounded-full bg-white/15 text-white">{notice}</span>}
        </div>
      )}

      {/* Viewfinder */}
      <div className="relative flex-1 min-h-0 bg-black overflow-hidden">
        <video
          ref={videoRef}
          playsInline
          muted
          autoPlay
          className="absolute inset-0 w-full h-full object-contain"
        />

        {isAfter && selectedBefore && ghostOpacity > 0 && (
          <img
            src={selectedBefore.url}
            alt=""
            className="absolute inset-0 w-full h-full object-contain pointer-events-none"
            style={{ opacity: ghostOpacity }}
          />
        )}

        {isAfter && selectedBefore && (
          <span className="absolute top-2 left-2 px-2 py-1 rounded-full bg-black/60 text-white text-xs">
            Matching: {selectedBefore.caption || 'No label'}
          </span>
        )}

        {flash && <div className="absolute inset-0 bg-white/70 pointer-events-none" />}

        {!ready && !cameraError && (
          <div className="absolute inset-0 flex items-center justify-center text-white/70">
            <Loader className="animate-spin" size={28} />
          </div>
        )}

        {cameraError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center text-white">
            <AlertCircle size={32} className="text-red-400" />
            <p className="text-sm">{cameraError}</p>
            <button onClick={requestClose} className="px-4 py-2 rounded-lg bg-white/15 text-sm">
              Close
            </button>
          </div>
        )}

        {allMatched && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center text-white bg-black/60">
            <Check size={32} className="text-green-400" />
            <p className="text-sm">
              {stripList.length === 0 ? 'No Before photos on this job yet.' : 'Every Before has an After.'}
            </p>
            <button onClick={requestClose} className="px-4 py-2 rounded-lg bg-green-600 text-sm font-medium">
              Done
            </button>
          </div>
        )}
      </div>

      {/* Bottom controls */}
      <div className="bg-black px-3 pt-3 pb-4 text-white space-y-3">
        {!isAfter && (
          <>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {labelOptions.map(opt => (
                <button
                  key={opt}
                  onClick={() => setLabel(label === opt ? '' : opt)}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm ${
                    label === opt ? 'bg-blue-600 text-white' : 'bg-white/15 text-white'
                  }`}
                >
                  {opt}
                </button>
              ))}
              <button
                onClick={() => setShowLabelInput(v => !v)}
                className="flex-shrink-0 px-3 py-1.5 rounded-full text-sm bg-white/15 text-white flex items-center gap-1"
              >
                <Plus size={14} /> New
              </button>
            </div>

            {showLabelInput && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const t = labelDraft.trim();
                  if (t) {
                    setLabel(t.slice(0, 60));
                    setLabelDraft('');
                    setShowLabelInput(false);
                  }
                }}
                className="flex gap-2"
              >
                <input
                  value={labelDraft}
                  onChange={(e) => setLabelDraft(e.target.value)}
                  maxLength={60}
                  placeholder="e.g. Oak by driveway"
                  className="flex-1 px-3 py-2 rounded-lg bg-white/10 text-white placeholder-white/40 text-sm focus:outline-none"
                  autoFocus
                />
                <button type="submit" className="px-3 py-2 rounded-lg bg-blue-600 text-sm">Use</button>
              </form>
            )}

            <p className="text-xs text-white/50 text-center">
              {label ? `Label: ${label}` : 'No label selected. Tap one above or add your own.'}
            </p>
          </>
        )}

        {isAfter && stripList.length > 0 && (
          <>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {stripList.map(b => {
                const done = pairedIds.has(b.id) || shotIds.has(b.id);
                const selected = b.id === selectedBeforeId;
                return (
                  <button
                    key={b.id}
                    id={`sf-before-${b.id}`}
                    onClick={() => setSelectedBeforeId(b.id)}
                    className={`relative flex-shrink-0 w-16 p-0.5 rounded-lg ${selected ? 'ring-2 ring-green-400' : ''}`}
                  >
                    <img
                      src={b.thumbnailUrl || b.url}
                      alt=""
                      className={`w-full h-16 rounded-md object-cover ${done ? 'opacity-50' : ''}`}
                    />
                    {done && (
                      <span className="absolute top-1 right-1 bg-green-500 rounded-full p-0.5">
                        <Check size={10} className="text-white" />
                      </span>
                    )}
                    <span className="block text-[10px] text-white/70 truncate mt-0.5">
                      {b.caption || 'No label'}
                    </span>
                  </button>
                );
              })}
            </div>
            {skippedHere > 0 && (
              <button
                onClick={() => onSkippedChange?.([])}
                className="block mx-auto text-xs text-white/60 underline"
              >
                Show {skippedHere} skipped
              </button>
            )}
          </>
        )}

        <div className="grid grid-cols-3 items-center">
          <div className="justify-self-start">
            {isAfter && (
              <button
                onClick={() => setGhostLevel(l => (l + 1) % GHOST_LEVELS.length)}
                className="flex flex-col items-center gap-1 p-2 text-xs text-white/80"
              >
                {ghostOpacity > 0 ? <Eye size={22} /> : <EyeOff size={22} />}
                {ghostOpacity > 0 ? `Ghost ${Math.round(ghostOpacity * 100)}%` : 'Ghost off'}
              </button>
            )}
          </div>

          <div className="justify-self-center">
            <button
              onClick={handleShutter}
              disabled={!ready || busy || (isAfter && !selectedBefore)}
              className="w-[72px] h-[72px] rounded-full border-4 border-white flex items-center justify-center disabled:opacity-40"
              aria-label="Take photo"
            >
              <span className="w-14 h-14 rounded-full bg-white" />
            </button>
          </div>

          <div className="justify-self-end">
            {isAfter ? (
              <button
                onClick={handleSkipSelected}
                disabled={!selectedBefore}
                className="flex flex-col items-center gap-1 p-2 text-xs text-white/80 max-w-[88px] text-center disabled:opacity-40"
              >
                <SkipForward size={22} />
                No after needed
              </button>
            ) : (
              <button
                onClick={requestClose}
                className="flex flex-col items-center gap-1 p-2 text-xs text-white/80"
              >
                <Check size={22} />
                Done
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Close guard: never silently drop photos that have not uploaded */}
      {showCloseGuard && (
        <div className="absolute inset-0 z-10 bg-black/80 flex items-center justify-center p-6">
          <div className="bg-white rounded-xl p-5 max-w-sm w-full text-center space-y-3">
            {pending > 0 ? (
              <>
                <Loader className="animate-spin mx-auto text-blue-600" size={28} />
                <p className="text-gray-900 font-medium">
                  Finishing {pending} upload{pending !== 1 ? 's' : ''}…
                </p>
                <p className="text-sm text-gray-500">This closes by itself when they are done.</p>
              </>
            ) : (
              <>
                <AlertCircle className="mx-auto text-red-500" size={28} />
                <p className="text-gray-900 font-medium">
                  {failed.length} photo{failed.length !== 1 ? 's' : ''} did not upload
                </p>
                <p className="text-sm text-gray-500">{failed[0]?.message}</p>
                <div className="flex gap-2">
                  <button
                    onClick={retryFailed}
                    className="flex-1 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium"
                  >
                    Retry
                  </button>
                  <button
                    onClick={() => setFailed([])}
                    className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium"
                  >
                    Discard &amp; close
                  </button>
                </div>
              </>
            )}
            <button onClick={() => setShowCloseGuard(false)} className="text-sm text-gray-500 underline">
              Back to camera
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return createPortal(content, document.body);
};

export default PhotoCapture;